import { useState, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'
import LinearProgress from '@mui/material/LinearProgress'
import Alert, { type AlertColor } from '@mui/material/Alert'
import CloseIcon from '@mui/icons-material/Close'
import {
  useAddNewPlateNumberMutation,
  useUpdateWhiteListMutation,
  useGetWhiteListByBuildingIdQuery,
} from '@/api/whiteList'
import { 
  selectUpsertModalParams, 
  selectShowUpsertModal,
  clearUpsertModalParams,
} from '../accessSlice'
import {
  selectActiveBuilding,
  selectActiveBuildingId 
} from '@/features/building/buildingSlice'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import UpsertModalContent from './UpsertModalContent'

const UpsertModal = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch();
  const show = useAppSelector(selectShowUpsertModal);
  const modalParams = useAppSelector(selectUpsertModalParams);
  const activeBuildingId = useAppSelector(selectActiveBuildingId);
  const activeBuilding = useAppSelector(selectActiveBuilding);
  const isEdit = Boolean(modalParams?.id)
  
  const [ updateList, { 
    isLoading: updateLoading, 
  }] = useUpdateWhiteListMutation()
  
  const [ addNewNumber, { 
    isLoading, 
  }] = useAddNewPlateNumberMutation()

  const { 
    data: whitelistItem,
    isLoading: isRecordQueryLoading,
  } = useGetWhiteListByBuildingIdQuery(modalParams?.requestParams ?? {}, {
    selectFromResult: ({ data, isLoading }) => ({
      data: data?.data?.find((item) => +item.id === +(modalParams?.id ?? 0)),
      isLoading,
    }),
    skip: !show || !modalParams?.id
  })

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: AlertColor
    message: string
  }>({
    open: false,
    severity: 'success',
    message: '',
  })
  const canMountForm = show && (!isEdit || whitelistItem || !isRecordQueryLoading)
  const isRecordLoading = show && isEdit && !canMountForm

  const handleSnackClose = (
    _event?: SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const handleClose = () => {
    dispatch(clearUpsertModalParams())
  }

  const onSaved = () => {
    dispatch(clearUpsertModalParams())
    setSnackbar({
      open: true,
      severity: 'success',
      message: t('saved'),
    })
  }

  const onError = (message: string) => {
    if (!message) return
    setSnackbar({
      open: true,
      severity: 'error',
      message,
    })
  }

  return (
    <>
      <Dialog
        open={show}
        onClose={handleClose}
        fullWidth
        disableEnforceFocus
      >
        {(isLoading || updateLoading || isRecordLoading) && <LinearProgress />}
        <DialogTitle>
          {modalParams?.id ? t('edit') : t('add_number')}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {canMountForm && (
          <UpsertModalContent
            key={modalParams?.id ?? 'create'}
            modalParams={modalParams}
            whitelistItem={whitelistItem}
            activeBuildingId={activeBuildingId}
            activeBuilding={activeBuilding}
            updateList={updateList}
            addNewNumber={addNewNumber}
            isLoading={isLoading}
            updateLoading={updateLoading}
            onSaved={onSaved}
            onError={onError}
          />
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        {
          snackbar.severity === 'success' ? (
            <Alert
              sx={{ width: '100%' }}
              severity="success"
              variant="filled"
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            >
              {snackbar.message}
            </Alert>
          ) : (
            <Alert 
              sx={{ width: '100%' }} 
              severity="error" 
              variant="filled"
            >
              {snackbar.message || t('error_occurred')}
            </Alert>
          )
        }
      </Snackbar>
    </>
  )
}

export default UpsertModal
